import {
  Component,
  ComponentRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { LeafletMapboxService } from '../../../../../services/leaflet-mapbox.service';
import {
  IdentifiableLayer,
  MapStore,
} from '../../../../../stores/map/map.state';
import { DataLayerTypeStore } from '../../../../../stores/data-layer-type/data-layer-type.state';
import {
  LatLng,
  Control,
  featureGroup,
  Map,
  Marker,
  Polygon,
  Polyline,
  LayerGroup,
  Layer,
} from 'leaflet';
import 'leaflet-gesture-handling';
import { LegendComponent } from '../../../../molecules/legend/legend.component';
import { DataEntry } from '../../../../../models/indicator-data';
import { BaseChartComponent } from '../../../base-chart/base-chart.component';
import { WidgetHelperService } from '../../../../../services/widget/widget-helper.service';
import { DataLayer } from '../../../../../models/data-layer';
import { GeoDataStore } from '../../../../../stores/geo/geo-data.state';
import { Indicator } from '../../../../../models/indicator';
import {
  GridWidgetConfigService,
  PoiConf,
  WegweiserIndicator,
  WidgetConfig,
} from '../../../../../services/grid-widget-config.service';
import { DividerModule } from 'primeng/divider';
import { ComparisonValueStore } from '../../../../../stores/comparison-value/comparison-value.state';
import { FeatureCollection } from '../../../../../models/geo-data';
import { SafeNumberPipe } from '../../../../../pipes/safe-number.pipe';
import * as _ from 'lodash';
import { MapCoordinatorService } from '../../../../../services/map/map-coordinator.service';
import { MapLegendService } from '../../../../../services/map/map-legend.service';
import { isObject } from 'chart.js/helpers';
import { PoiIsochroneService } from '../../../../../services/isochrone/poi-isochrone.service';

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [DividerModule, SafeNumberPipe],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss',
  providers: [
    LeafletMapboxService,
    MapStore,
    MapCoordinatorService,
    PoiIsochroneService,
  ],
})
export class MapComponent
  extends BaseChartComponent
  implements OnInit, OnDestroy
{
  @ViewChild('chartComp', { static: true }) override chartComp!: any;

  readonly mapStore = inject(MapStore);
  readonly comparisonStore = inject(ComparisonValueStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly geoStore = inject(GeoDataStore);
  readonly coordinator = inject(MapCoordinatorService);
  readonly legendService = inject(MapLegendService);
  readonly isochroneService = inject(PoiIsochroneService);

  public mapId = '';
  public dataLayer: DataLayer | null = null;
  private intersectionObserver?: IntersectionObserver;
  public geoJson: FeatureCollection | null = null;
  public group?: string = undefined;
  public mapTooltipPosition = 'leaflet-right leaflet-top';
  public mapStyleClass = '';
  public isPointMap = false;

  private legendComponentRef?: ComponentRef<LegendComponent>;
  private legendControl?: Control;
  private map!: Map;
  private mapConfig!: WidgetConfig;
  private mapData?: DataEntry;
  private zoomStep = 0.5;
  private initialViewAdjusted = false;
  private previousChartData?: any;
  private previousMapConfig?: WidgetConfig;

  constructor(
    widgetHelperService: WidgetHelperService,
    private configService: GridWidgetConfigService
  ) {
    super(widgetHelperService);
    this.mapId = 'map_' + Date.now();
  }

  /**
   * Initializes the component by setting up required observers and invoking appropriate initialization logic.
   * If the map is already initialized, it immediately calls the parent class's initialization method.
   * Otherwise, it sets up an IntersectionObserver to handle deferred initialization when the element becomes visible.
   *
   * @return {void} Does not return any value.
   */
  override init(): void {
    if (this.map) {
      super.init();
      return;
    }
    const el = this.chartComp?.nativeElement;
    if (!el) {
      queueMicrotask(() => {
        this.initMap();
        super.init();
      });
      return;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          this.intersectionObserver?.disconnect();
          this.intersectionObserver = undefined;
          this.initMap();
          super.init();
        }
      },
      { threshold: 0.1 }
    );
    this.intersectionObserver.observe(el);
  }

  /**
   * Updates the chart by re-rendering the map after a delay.
   * Ensures that the chart is only updated if it has been initialized.
   * The rendering process is delayed to allow for any potential asynchronous operations.
   *
   * @return {void} Does not return a value.
   */
  override updateChart(): void {
    if (!this.initialized) return;
    const newChartData = this.widgetStore.getChartData(this.id);

    this.chartData = newChartData;
    if (this.mapRerenderGuard()) {
      const oldConfig = this.previousMapConfig;
      const newConfig = this.config?.widget;

      const isochroneConfigChanged =
        oldConfig?.map?.isochroneProfile !== newConfig?.map?.isochroneProfile ||
        oldConfig?.map?.isochroneColorset !==
          newConfig?.map?.isochroneColorset ||
        !_.isEqual(
          oldConfig?.map?.isochroneMinutes,
          newConfig?.map?.isochroneMinutes
        );

      if (isochroneConfigChanged) {
        this.refreshActiveIsochrones();
      }

      this.previousChartData = newChartData;
      this.previousMapConfig = _.cloneDeep(this.config?.widget);
      setTimeout(() => {
        this.setConfig();
        this.renderMap();
      }, 30);
    }
  }

  /**
   * Removes all isochrone layers from the map and store.
   *
   * @private
   */
  private clearIsochrones(): void {
    const entities = this.mapStore.entityMap();
    Object.keys(entities).forEach(id => {
      if (id.startsWith('isochrone-')) {
        const layer = entities[id];
        if (layer && this.map) {
          this.map.removeLayer(layer);
        }
        this.mapStore.removeLayer(id);
      }
    });
  }

  /**
   * Determines whether the chart should be re-rendered by comparing the current chart data,
   * the previous chart data, and certain configuration aspects.
   *
   * @return {boolean} Returns `true` if a re-render is necessary, otherwise `false`.
   */
  mapRerenderGuard(): boolean {
    if (!this.previousChartData) return true;
    if (!this.chartData) return true;
    if (
      this.previousChartData.datasets.length !== this.chartData.datasets.length
    )
      return true;
    const oldDataKeys = this.previousChartData.datasets.map(
      (dat: any) => dat.labelKey
    );
    const newDataKeys = this.chartData.datasets.map((dat: any) => dat.labelKey);

    if (oldDataKeys.join() !== newDataKeys.join()) return true;
    const oldConfig = this.previousMapConfig;
    const newConfig = this.config?.widget;
    if (!oldConfig || !newConfig) return true;
    if (oldConfig.disabledCharts?.length !== newConfig.disabledCharts?.length)
      return true;
    if (this.coordinator.checkHighlightChange()) return true;
    if (!_.isEqual(oldConfig, newConfig)) return true;
    return false;
  }

  override handleEvent(event: string) {
    switch (event) {
      case 'centerMap':
        this.centerMap();
        break;
      case 'showPoi':
        this.showPoiPopup(this.widgetStore.eventOptions().key);
        break;
      case 'toggleIsochrone':
        this.togglePoiIsochrone(this.widgetStore.eventOptions().key);
        break;
    }
  }

  /**
   * Initializes the map and sets up the necessary event listeners for tracking the map's state.
   *
   * This method retrieves the DOM element for the map container, initializes the map using a map store,
   * and binds specific event handlers such as `load` and `dragend` to perform actions like saving the map state.
   *
   * @return {void} This method does not return a value.
   */
  initMap(): void {
    const mapContainer = this.chartComp.nativeElement; // Get the DOM element reference

    this.map = this.coordinator.initializeMap(this.mapId, mapContainer);
    this.map.off('moveend');

    this.map.on('load', () => {
      this.map.on('dragend', () => {
        this.saveMapView();
      });
    });
  }

  /**
   * Updates the mapData property based on the first dataset in the chartData and the widget features configuration.
   * This method maps the data from the first dataset to the configured features in the widget.
   *
   * @return {void} Does not return anything.
   */
  setMapDataByFirstDataSet(): void {
    if (!this.config?.widget) return;
    const firstDataSet: any = this.chartData?.datasets[0];
    const labels: any[] = this.chartData?.labels || [];
    const features = this.config.widget.features;
    const data: DataEntry = {};

    if (firstDataSet && features) {
      for (const key in features) {
        const feature = features[key];
        const dataIdx = labels.indexOf(feature.name);
        data[key] = firstDataSet.data[dataIdx];
      }
    }

    this.mapData = data;
  }

  /**
   * Sets the data layer for the instance based on the configuration provided in `mapConfig`.
   * Retrieves the appropriate data layer using the `dataLayerStore` if a valid `dataLayerId` exists in `mapConfig`.
   * If no `dataLayerId` is defined, the data layer is set to null.
   *
   * @return {void} This method does not return a value.
   */
  setDataLayer(): void {
    const dataLayerId = this.mapConfig?.dataLayer;
    this.dataLayer = dataLayerId
      ? this.dataLayerStore.getById(dataLayerId)
      : null;
    this.isPointMap = this.dataLayer?.type === 'Point';
  }

  /**
   * Updates the map configuration by retrieving the configuration data
   * either from the existing widget configuration or the configuration service.
   * The updated configuration is then set in the map store.
   *
   * @return {void} Does not return a value.
   */
  setConfig(): void {
    this.mapConfig =
      this.config?.widget || this.configService.getWidgetConfig({}, 'map');

    this.coordinator.setMapConfig(this.mapConfig);
    this.setMapTooltipPosition();
    this.setTooltipStyle();
  }

  private setTooltipStyle() {
    this.mapStyleClass =
      this.mapConfig.map?.captions.style === 'blank' ? 'tooltip-blank' : '';
  }

  /**
   * Sets the map tooltip position based on the map configuration and legend properties.
   *
   * This method determines the tooltip's position dynamically by evaluating the `position`
   * and `size` properties of the configured map legend. If these properties are not defined,
   * the default position is assigned as 'leaflet-right leaflet-top'.
   * Depending on the combination of `position` and `size` values, different tooltip positions
   * are assigned to `this.mapTooltipPosition`.
   *
   * @return {void} No return value.
   */
  setMapTooltipPosition() {
    const position = this.mapConfig.map?.legend?.position;
    const size = this.mapConfig.map?.legend?.size;

    if (!position || !size) {
      this.mapTooltipPosition = 'leaflet-right leaflet-top';
    } else {
      switch (position + size) {
        case 'topleftsmall':
          this.mapTooltipPosition = 'leaflet-right leaflet-bottom';
          break;
        case 'toprightsmall':
          this.mapTooltipPosition = 'leaflet-left leaflet-bottom';
          break;
        case 'toprightlarge':
          this.mapTooltipPosition = 'leaflet-left leaflet-top';
          break;
        case 'topleftlarge':
        case 'bottomleftsmall':
        case 'bottomleftlarge':
          this.mapTooltipPosition = 'leaflet-right leaflet-top';
          break;
        case 'bottomrightsmall':
        case 'bottomrightlarge':
          this.mapTooltipPosition = 'leaflet-left leaflet-top';
          break;
      }
    }
  }

  /**
   * Sets the GeoJSON data for the map configuration based on the current context
   * and available geo data in the data layer.
   *
   * @return {void} Does not return any value.
   */
  setGeoJson(): void {
    if (this.mapConfig.context !== 'external') {
      this.geoJson =
        this.dataLayer && this.dataLayer.geoData
          ? this.geoStore.geoJsonById(this.dataLayer.geoData, this.group)
          : null;
    }
  }

  /**
   * Sets the geoJson property to the geoData of the wegweiserData object from the map configuration.
   * Updates the internal state with the geographic data for further processing.
   *
   * @return {void} Does not return a value.
   */
  setWegweiserGeoData(): void {
    this.geoJson = this.mapConfig.wegweiserData?.geoData;
  }

  /**
   * Sets the group property of the current instance based on the active group
   * from the configuration's widget. If no active group is provided, sets it to undefined.
   *
   * @return {void} Does not return a value.
   */
  setGroup(): void {
    this.group = this.config?.widget.activeGroup || undefined;
  }

  /**
   * Initializes the setup for the map based on the configuration.
   * This method checks if the map configuration exists and sets up
   * the appropriate data layers, groups, and GeoJSON data depending
   * on the context of the configuration. If the context is external,
   * it sets up Wegweiser GeoData instead.
   *
   * @return {void} Does not return any value.
   */
  mapSetupInit(): void {
    this.setConfig();
    if (!this.mapConfig) return;
    if (this.mapConfig.context !== 'external') {
      this.setDataLayer();
      this.setGroup();
      this.setGeoJson();
    } else {
      this.setWegweiserGeoData();
    }
  }

  /**
   * Creates and adds a GeoJSON layer to the map using the provided
   * GeoJSON data, map configuration, and associated chart data.
   *
   * @return {void} This method does not return a value.
   */
  createGeoJsonLayer(): void {
    if (this.isPointMap && this.geoJson && this.mapConfig.dataLayerType) {
      this.dataLayerTypeStore
        .getPoiIcon(this.mapConfig.dataLayerType)
        ?.subscribe(icon => {
          if (!this.geoJson) return;

          this.coordinator.createGeoJsonLayer(
            this.mapData,
            this.geoJson,
            this.mapConfig,
            this.chartData,
            icon
          );
        });
    } else {
      queueMicrotask(() => {
        if (this.geoJson) {
          this.coordinator.createGeoJsonLayer(
            this.mapData,
            this.geoJson,
            this.mapConfig,
            this.chartData,
            this.isPointMap
          );
        }
      });
    }
  }

  /**
   * Renders and initializes the map with configuration and data layers.
   * This includes setting up the map, initializing layers, configuring data,
   * and adjusting the map view as needed.
   *
   * @return {void}
   */
  renderMap(): void {
    this.mapSetupInit();
    this.initializePointLayer();

    if (
      (this.mapConfig.context !== 'external' &&
        (!this.dataLayer || !this.dataLayer.geoData)) ||
      (this.mapConfig.context === 'external' &&
        !this.mapConfig.wegweiserData?.geoData)
    )
      return;

    this.setMapDataByFirstDataSet();

    this.createGeoJsonLayer();
    setTimeout(() => {
      this.addLegendComponent(this.mapStore.colors() || []);
      if (this.map && !this.initialViewAdjusted) this.adjustMap();
    }, 300);
  }

  /**
   * Adjusts and centers the map view to fit all visible markers, polygons, or polylines
   * on the map.
   * Tries to calculate bounds from all layers first, including `LayerGroup` and
   * `FeatureGroup`, before falling back to a GeoJSON Layer if available and applicable.
   *
   * @return {void} No return value.
   */
  centerMap(): void {
    if (!this.map) return;
    setTimeout(() => {
      const allLayers = featureGroup();
      let bounds = undefined;
      this.map.eachLayer(layer => {
        if (
          layer instanceof Marker ||
          layer instanceof Polygon ||
          layer instanceof Polyline
        ) {
          allLayers.addLayer(layer);
        }
      });

      bounds = allLayers.getBounds();
      // Handle LayerGroups or FeatureGroups
      if (!bounds.isValid()) {
        this.map.eachLayer(layer => {
          if (layer instanceof LayerGroup) {
            layer.eachLayer((childLayer: Layer) => {
              if (
                childLayer instanceof Marker ||
                childLayer instanceof Polygon ||
                childLayer instanceof Polyline
              ) {
                allLayers.addLayer(childLayer);
              }
            });
          }
        });
        bounds = allLayers.getBounds();
      }

      // Get bounds of all layers and fit the ma
      const geoLayer = this.mapStore.geoJsonLayer();

      if (
        bounds.isValid() &&
        bounds.getSouthWest().equals(bounds.getNorthEast())
      ) {
        // Only one point is present
        this.map.setView(bounds.getCenter(), 15); // 15 = a reasonable zoom level, adjust as needed
      } else if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [20, 20] }); // Optional padding
      } else if (geoLayer) {
        this.map.fitBounds(geoLayer.getBounds(), { padding: [20, 20] });
      }
    }, 100);
  }

  /**
   * Displays a popup for a point of interest (POI) on the map, based on the provided unique key.
   * If the popup is already open, it will be closed. Otherwise, the popup will be opened.
   *
   * @param {string} key - The unique identifier of the POI to display the popup for. This can match either the `key` property or the `id` of the feature.
   * @return {void} Does not return any value.
   */
  showPoiPopup(key: string) {
    const entities = this.mapStore.entityMap(); // Access the entity map from MapStore

    for (const layer of Object.values(entities) as IdentifiableLayer[]) {
      // Check if layer is a LayerGroup (which includes GeoJSON layers)
      if (layer instanceof LayerGroup) {
        let found = false;

        layer.eachLayer((subLayer: any) => {
          if (found) return; // Skip if already found
          // Check if the sublayer has a feature with matching key
          if (
            subLayer.feature &&
            (subLayer.feature.properties?.key === key ||
              subLayer.feature.id === key)
          ) {
            // Center the map on the feature
            if (subLayer.getLatLng) {
              this.map.setView(
                subLayer.getLatLng(),
                Math.max(this.map.getZoom(), 15)
              );
            } else if (subLayer.getBounds) {
              this.map.fitBounds(subLayer.getBounds(), { padding: [20, 20] });
            }

            subLayer.openPopup();
            found = true;
          }
        });

        if (found) {
          break; // Stop searching once popup is opened
        }
      }
    }
  }

  /**
   * Toggles the isochrone layer for a specific point of interest (POI) on the map, based on the provided unique key.
   *
   * @param {string} key - The unique identifier of the POI to toggle the isochrone for.
   * @return {void}
   */
  togglePoiIsochrone(key: string) {
    const feature = this.findFeatureByKey(key);
    if (feature) {
      this.isochroneService.toggleIsochroneForFeature(feature);
    }
  }

  /**
   * Initializes the point layers on the map based on the configuration and available data.
   * This method processes the `poi` (points of interest) configuration from `mapConfig`, filters
   * relevant data layers, retrieves the geospatial data and indicators, and adds the corresponding
   * point layers to the map.
   *
   * The method also ensures any existing point layers are removed before initializing the new layers.
   * If no `poi` configuration is detected, it will simply remove all existing point layers.
   *
   * @return {void} Does not return a value. The method operates by side effects on the map and related stores.
   */
  initializePointLayer(): void {
    const poi =
      this.mapConfig.map &&
      isObject(this.mapConfig.map?.poi) &&
      !Array.isArray(this.mapConfig.map?.poi)
        ? this.convertOldPoiToPoiConfHook(this.mapConfig.map.poi)
        : this.mapConfig.map?.poi;

    if (poi && poi.length > 0) {
      const year = Number(this.mapConfig.year);
      //this.mapStore.removeAllPointLayer();

      const currentPoiIds = poi.map((p: PoiConf) => p.type);
      const existingLayers = this.mapStore.entityMap();
      const existingLegends = this.mapStore.indicatorLegends();

      // Remove layers and legends not in current config
      for (const layerId in existingLayers) {
        if (layerId.startsWith('isochrone-')) continue;
        if (!currentPoiIds.includes(layerId)) {
          this.coordinator.removePointLayer(layerId);

          // Also remove associated legend
          const legend = this.mapStore.getIndicatorLegend(layerId);
          if (legend) {
            this.legendService.removeLegend(this.map, legend);
            this.mapStore.removeIndicatorLegend(layerId);
          }
        }
      }

      // Remove orphaned legends (legends without corresponding layers)
      for (const legendId in existingLegends) {
        if (!currentPoiIds.includes(legendId) && !existingLayers[legendId]) {
          const legend = this.mapStore.getIndicatorLegend(legendId);
          if (legend) {
            this.legendService.removeLegend(this.map, legend);
            this.mapStore.removeIndicatorLegend(legendId);
          }
        }
      }

      for (const poiConfig of poi) {
        const poiId = poiConfig.type;
        if (this.mapRerenderGuard()) {
          continue;
        }
        const poiLayer: DataLayer = this.dataLayerStore.filterByParentAndYear(
          poiId,
          year
        );

        if (!poiLayer || !poiLayer.geoData) continue;
        const geoJson = this.geoStore.geoJsonById(
          poiLayer.geoData,
          this.mapConfig.map?.groups?.[poiId] || undefined
        );

        if (!geoJson) continue;

        const indicatorIds = poiConfig?.indicators;
        const indicators: Indicator[] = [];
        const indicatorDataArray: DataEntry[] = [];

        // Handle both single indicator (backward compatibility) and multiple indicators
        if (indicatorIds) {
          const idsArray = Array.isArray(indicatorIds)
            ? indicatorIds
            : [indicatorIds];

          idsArray.forEach(indicatorId => {
            if (indicatorId) {
              const indicator = this.indicatorStore.getById(indicatorId);
              const indicatorData =
                this.indicatorDataStore.getByDataLayerAndIndicator(
                  poiLayer._id,
                  indicatorId
                );
              if (indicator) {
                indicators.push(indicator);
              }
              if (indicatorData?.data) {
                indicatorDataArray.push(indicatorData.data);
              }
            }
          });
        }

        this.dataLayerTypeStore.getPoiIcon(poiId)?.subscribe(icon => {
          if (!icon) return;
          this.coordinator.addPointLayer(
            poiConfig,
            this.dataLayerTypeStore.getById(poiId)?.name || '',
            indicatorDataArray,
            geoJson,
            icon,
            indicators
          );
        });
      }

      this.restoreIsochrones();

      // Check if we should show a popup for a POI after all layers are initialized
      const pendingPopupKey = this.widgetStore.eventOptions()?.key;
      if (pendingPopupKey) {
        setTimeout(() => this.showPoiPopup(pendingPopupKey), 250);
      }
    } else {
      this.coordinator.removeAllPointLayers(true);
    }
  }

  /**
   * Converts a POI (Point of Interest) object into an array of PoiConf objects as a fallback configuration.
   * Once new PoiConf for individual POI styling was added, this method is used as a fallback for older bookmarks.
   *
   * @param poi The input object containing POI data, where keys represent types and values include associated indicators.
   *
   * @return An array of PoiConf objects, each representing a type and its corresponding indicators.
   */
  private convertOldPoiToPoiConfHook(poi: any): PoiConf[] {
    const result: PoiConf[] = [];
    Object.keys(poi).forEach(key => {
      result.push({
        type: key,
        indicators: poi[key].indicators,
      });
    });

    return result;
  }

  /**
   * Adds a legend component to the map based on the provided colors, scales, and indicator.
   * Removes any existing legend component before adding the new one.
   *
   * @param {string[]} colors - An array of color codes used for the legend.
   * @return {void} This method does not return a value.
   */
  private addLegendComponent(colors: string[]): void {
    const scales = this.mapStore.scales();
    const indicator: Indicator | WegweiserIndicator | null =
      this.mapConfig.context !== 'external'
        ? this.mapStore.indicator()
        : this.mapConfig.wegweiserData?.indicators.at(0) || null;

    this.removeLegendComponent();

    if (!scales || !indicator) return;

    const legendSize = this.mapConfig?.map?.legend.size;
    const validSize: 'small' | 'large' =
      legendSize === 'small' || legendSize === 'large' ? legendSize : 'large';

    const legend = this.legendService.addLegendComponent(
      this.map,
      scales,
      colors,
      indicator,
      [],
      this.mapConfig?.map?.legend.position || 'bottomright',
      validSize,
      this.mapConfig?.comparisonValues,
      this.comparison || undefined
    );
    this.legendComponentRef = legend.legendComponentRef;
    this.legendControl = legend.legendControl;
  }

  /**
   * Removes the legend component and its associated control from the map.
   * If the legend component or control does not exist, the method will exit without performing any action.
   *
   * @return {void} This method does not return a value.
   */
  removeLegendComponent(): void {
    if (!this.legendComponentRef || !this.legendControl) return;

    this.legendService.removeLegend(this.map, {
      legendComponentRef: this.legendComponentRef,
      legendControl: this.legendControl,
    });
    this.legendComponentRef = undefined;
    this.legendControl = undefined;
  }

  /**
   * Adjusts the map's view settings by either centering it based on configuration or using a default center method.
   * This method ensures the map renders correctly and applies the desired bounds and zoom level if provided.
   *
   * @return {void} Does not return a value.
   */
  private adjustMap(): void {
    if (!this.map) return;
    //this.map.invalidateSize(); // workaround rendering delay
    const view = this.mapConfig.map?.view;

    if (view && view.bounds?.lat) {
      const boundsFromConfig = new LatLng(view.bounds.lat, view.bounds.lng);
      const zoomLevel = view.zoom;

      this.map.setView(boundsFromConfig, zoomLevel);
    } else {
      this.centerMap();
    }

    this.initialViewAdjusted = true;
  }

  /**
   * Updates and saves the current map view configuration, including the map's center position
   * and zoom level, to the widget store. Triggers layout changes within the grid store.
   *
   * @return {void} This method does not return a value.
   */
  private saveMapView(): void {
    if (!this.map || !this.mapConfig.map || !this.geoJson) return;
    const bounds = this.map.getCenter();
    const zoom = this.map.getZoom();
    const newMapConfig = {
      ...this.mapConfig.map,
      ...{ view: { bounds, zoom } },
    };

    this.widgetStore.setWidgetProperty(
      'map',
      newMapConfig,
      this.config?.id,
      false
    );
    this.gridStore.triggerLayoutChanges();
  }

  /**
   * Retrieves the comparison data based on the current indicator.
   *
   * The method checks for the presence of a valid indicator and fetches its associated
   * comparison values from the configuration. If valid comparison data exists, it
   * constructs and returns an object where keys represent comparison categories,
   * and values represent the numeric comparison values. If no valid comparison data
   * is found, it returns null.
   *
   * @return {null | { [key: string]: number }} An object containing comparison data
   *                                           with keys as category names and values
   *                                           as corresponding numerical data, or null
   *                                           if no valid data is available.
   */
  get comparison(): null | { [key: string]: number } {
    const indicator = this.mapStore.indicator();
    if (!indicator) return null;

    const compVals: { [key: string]: number } = {};
    const comparisons = this.config?.widget?.groupedData?.comparison;
    if (!comparisons) return null;
    for (const compKey in comparisons) {
      const values = comparisons[compKey];
      if (!values[indicator._id]) continue;
      compVals[compKey] = Object.values(values[indicator._id]).at(0) as number;
    }
    return Object.keys(compVals).length ? compVals : null;
  }

  /**
   * Callback method triggered when a resize event occurs.
   * Ensures that the underlying map correctly adjusts its size.
   *
   * @return {void} No return value.
   */
  override resizeCallBack(): void {
    if (this.map) {
      this.map.invalidateSize();
    }
  }

  /**
   * Increases the zoom level of the map by a predefined step.
   * Updates the map view after changing the zoom level.
   *
   * @return {void} This method does not return a value.
   */
  zoomIn(): void {
    this.map.setZoom(this.map.getZoom() + this.zoomStep);
    this.saveMapView();
  }

  /**
   * Decreases the zoom level of the map by a predefined step.
   *
   * This method reduces the current zoom level of the map incrementally,
   * adjusting the view to appear farther out. After updating the zoom level,
   * it saves the current state of the map view.
   *
   * @return {void} Does not return a value.
   */
  zoomOut(): void {
    this.map.setZoom(this.map.getZoom() - this.zoomStep);
    this.saveMapView();
  }

  /**
   * Lifecycle hook called when the component is destroyed.
   * Cleans up resources associated with the map, including removing all event listeners
   * and disposing of the map instance to prevent memory leaks.
   *
   * @return {void} This method does not return a value.
   */
  override ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;
    super.ngOnDestroy();
    if (this.map) {
      this.map.off(); // Remove all map event listeners
      this.map.remove(); // Clean up map instance
    }
  }

  /**
   * Opens the Point of Interest (POI) switch by triggering the configuration setup in the widget store.
   *
   * @return {void} No return value.
   */
  openPoiSwitch(): void {
    this.widgetStore.openConfig(this.id, true, true);
  }

  /**
   * Refreshes all active isochrones by re-fetching and re-rendering them.
   *
   * @private
   */
  private refreshActiveIsochrones(): void {
    const activeIsochrones = this.mapConfig?.map?.activeIsochrones;
    if (!activeIsochrones || activeIsochrones.length === 0) return;

    activeIsochrones.forEach(id => {
      const feature = this.findFeatureByKey(id);
      if (feature) {
        this.isochroneService.updateIsochroneForFeature(feature);
      }
    });
  }

  /**
   * Searches for a feature with the given key across all layers in the map store.
   *
   * @param {string} key - The key to search for.
   * @return {any | null} The found feature or null.
   * @private
   */
  private findFeatureByKey(key: string): any {
    const entities = this.mapStore.entityMap();

    for (const layer of Object.values(entities) as IdentifiableLayer[]) {
      if (layer instanceof LayerGroup) {
        let foundFeature: any = null;

        layer.eachLayer((subLayer: any) => {
          if (foundFeature) return;
          if (
            subLayer.feature &&
            (subLayer.feature.properties?.key === key ||
              subLayer.feature.id === key)
          ) {
            foundFeature = subLayer.feature;
          }
        });

        if (foundFeature) return foundFeature;
      }
    }
    return null;
  }

  /**
   * Restores active isochrones from the widget configuration.
   * This is used when the map is re-rendered or initialized to ensure
   * previously active isochrones are shown.
   *
   * @private
   */
  private restoreIsochrones(): void {
    const activeIsochrones = this.mapConfig.map?.activeIsochrones;
    if (!activeIsochrones || activeIsochrones.length === 0) return;

    // We use a small timeout to ensure point layers are added to the map
    // so we can find the features and their coordinates.
    setTimeout(() => {
      activeIsochrones.forEach(id => {
        const layerId = `isochrone-${id}`;
        if (!this.mapStore.getLayer(layerId)) {
          this.togglePoiIsochrone(id);
        }
      });
    }, 500);
  }
}
